import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, sankeyLeft } from 'd3-sankey';

interface SankeyChartProps {
  income: number;
  categoryTotals: { category: string; total: number; color: string }[];
  width?: number;
  height?: number;
}

const SankeyChart: React.FC<SankeyChartProps> = ({
  income,
  categoryTotals,
  width = 800,
  height = 400,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || income <= 0) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current);
    const margin = { top: 20, right: 140, bottom: 20, left: 100 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create gradient definitions
    const defs = svg.append('defs');

    // Calculate savings
    const totalExpenses = categoryTotals.reduce((sum, c) => sum + c.total, 0);
    const savings = Math.max(0, income - totalExpenses);

    // Build all outflows (categories + savings) and sort by size descending
    const allOutflows = [
      ...categoryTotals.filter(c => c.total > 0).map(c => ({
        name: c.category,
        value: c.total,
        color: c.color,
      })),
      ...(savings > 0 ? [{ name: 'Savings', value: savings, color: '#10b981' }] : []),
    ].sort((a, b) => b.value - a.value);

    // Build nodes: Income + sorted outflows
    const nodes: { name: string; color: string }[] = [
      { name: 'Income', color: '#3b82f6' },
      ...allOutflows.map(o => ({ name: o.name, color: o.color })),
    ];

    // Build links from Income to each outflow
    const links: { source: number; target: number; value: number }[] = allOutflows.map((outflow, idx) => ({
      source: 0,
      target: idx + 1,
      value: outflow.value,
    }));

    // Create sankey generator
    const sankeyGenerator = sankey<{ name: string; color: string }, {}>()
      .nodeWidth(20)
      .nodePadding(16)
      .nodeAlign(sankeyLeft)
      .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]]);

    const { nodes: sankeyNodes, links: sankeyLinks } = sankeyGenerator({
      nodes: nodes.map(d => ({ ...d })),
      links: links.map(d => ({ ...d })),
    });

    // Create gradients for each link
    sankeyLinks.forEach((link: any, i) => {
      const sourceColor = '#3b82f6'; // Income blue
      const targetColor = (link.target as any).color || '#94a3b8';

      const gradient = defs.append('linearGradient')
        .attr('id', `gradient-${i}`)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', (link.source as any).x1)
        .attr('x2', (link.target as any).x0);

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', sourceColor)
        .attr('stop-opacity', 0.6);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', targetColor)
        .attr('stop-opacity', 0.6);
    });

    // Draw links with gradients
    svg.append('g')
      .attr('fill', 'none')
      .selectAll('path')
      .data(sankeyLinks)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d: any, i: number) => `url(#gradient-${i})`)
      .attr('stroke-width', (d: any) => Math.max(2, d.width))
      .attr('opacity', 0.7)
      .style('mix-blend-mode', 'normal')
      .on('mouseover', function() {
        d3.select(this).attr('opacity', 1);
      })
      .on('mouseout', function() {
        d3.select(this).attr('opacity', 0.7);
      });

    // Draw nodes
    svg.append('g')
      .selectAll('rect')
      .data(sankeyNodes)
      .join('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => Math.max(4, d.y1 - d.y0))
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => d.color)
      .attr('rx', 3)
      .attr('ry', 3);

    // Add labels on the right side of nodes (except Income which is on left)
    svg.append('g')
      .style('font-size', '13px')
      .style('font-weight', '500')
      .style('font-family', 'Inter, system-ui, -apple-system, sans-serif')
      .selectAll('text')
      .data(sankeyNodes)
      .join('text')
      .attr('x', (d: any) => d.name === 'Income' ? d.x0 - 10 : d.x1 + 10)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', '-0.2em')
      .attr('text-anchor', (d: any) => d.name === 'Income' ? 'end' : 'start')
      .attr('fill', '#f8fafc')
      .text((d: any) => d.name);

    // Add value labels below name (with percentage of income)
    svg.append('g')
      .style('font-size', '12px')
      .style('font-family', 'Inter, system-ui, -apple-system, sans-serif')
      .selectAll('text')
      .data(sankeyNodes)
      .join('text')
      .attr('x', (d: any) => d.name === 'Income' ? d.x0 - 10 : d.x1 + 10)
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', '1em')
      .attr('text-anchor', (d: any) => d.name === 'Income' ? 'end' : 'start')
      .attr('fill', '#94a3b8')
      .text((d: any) => {
        const value = d.value || 0;
        const percentage = income > 0 ? ((value / income) * 100).toFixed(1) : '0.0';
        const dollarAmount = `$${value.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        return `${dollarAmount} (${percentage}%)`;
      });

  }, [income, categoryTotals, width, height]);

  if (income <= 0) {
    return (
      <div style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#94a3b8',
        fontSize: '0.875rem'
      }}>
        Set your monthly income to see the budget flow
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{ overflow: 'visible' }}
    />
  );
};

export default SankeyChart;
